/**
 * i18next setup. Arabic (RTL) is the primary language, English (LTR) secondary.
 *
 * Namespaces:
 *  - kernel namespaces from `src/i18n/locales/{ar,en}/{common,core,iam}.json`
 *  - module namespaces discovered automatically from the module folders:
 *      src/modules/<code>/locales/ar.json      → namespace `<code>`
 *      src/modules/<code>/locales/en.json
 *    or, for several namespaces per module:
 *      src/modules/<code>/locales/<lang>/<ns>.json → namespace `<ns>`
 *
 * Always reference keys with their namespace: `t('prd:orders.title')`.
 */
import i18n, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { storage } from '@web/lib/storage';
import { apiConfig } from '@web/lib/api';

export const SUPPORTED_LANGUAGES = ['ar', 'en'] as const;
export type Lang = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Lang = 'ar';
const LANG_KEY = 'kfms.lang';

type Json = Record<string, unknown>;

const kernelFiles = import.meta.glob<Json>('./locales/*/*.json', { eager: true, import: 'default' });
const moduleFlatFiles = import.meta.glob<Json>('../modules/*/locales/*.json', { eager: true, import: 'default' });
const moduleNestedFiles = import.meta.glob<Json>('../modules/*/locales/*/*.json', { eager: true, import: 'default' });

function isLang(v: string | undefined): v is Lang {
  return v === 'ar' || v === 'en';
}

function addBundle(resources: Resource, lang: string, ns: string, content: Json): void {
  if (!isLang(lang)) return;
  const bucket: ResourceLanguage = (resources[lang] ??= {});
  const existing = (bucket[ns] as Json | undefined) ?? {};
  bucket[ns] = { ...existing, ...content };
}

/** Collects every translation file into an i18next `Resource`. Exported for tests/tools. */
export function buildResources(): Resource {
  const resources: Resource = {};
  for (const [path, content] of Object.entries(kernelFiles)) {
    const m = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (m) addBundle(resources, m[1]!, m[2]!, content);
  }
  for (const [path, content] of Object.entries(moduleFlatFiles)) {
    const m = /\.\.\/modules\/([^/]+)\/locales\/([^/]+)\.json$/.exec(path);
    if (m) addBundle(resources, m[2]!, m[1]!, content);
  }
  for (const [path, content] of Object.entries(moduleNestedFiles)) {
    const m = /\.\.\/modules\/[^/]+\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (m) addBundle(resources, m[1]!, m[2]!, content);
  }
  return resources;
}

const resources = buildResources();
export const NAMESPACES = Array.from(new Set(Object.values(resources).flatMap((r) => Object.keys(r))));

export function storedLanguage(): Lang | null {
  const v = storage.get(LANG_KEY) ?? undefined;
  return isLang(v) ? v : null;
}

export function initialLanguage(): Lang {
  return storedLanguage() ?? DEFAULT_LANGUAGE;
}

export function directionOf(lang: string): 'rtl' | 'ltr' {
  return lang === 'ar' ? 'rtl' : 'ltr';
}

function applyDocumentLanguage(lang: Lang): void {
  const root = document.documentElement;
  root.setAttribute('lang', lang);
  root.setAttribute('dir', directionOf(lang));
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE === 'ar' ? ['ar', 'en'] : ['en'],
  ns: NAMESPACES,
  defaultNS: 'common',
  fallbackNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

applyDocumentLanguage(i18n.language as Lang);
apiConfig.setLanguageGetter(() => (isLang(i18n.language) ? i18n.language : DEFAULT_LANGUAGE));

/**
 * Switches the UI language (also updates `<html lang dir>`).
 * @param persist store as an explicit device preference (default true)
 */
export async function setLanguage(lang: Lang, persist = true): Promise<void> {
  if (persist) storage.set(LANG_KEY, lang);
  applyDocumentLanguage(lang);
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
}

export function currentLanguage(): Lang {
  return isLang(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

export default i18n;
